import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneSheet } from '../../chats/phone/PhoneSheet'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useToast } from '../../library/useToast'
import { accountOf, asList, num, unwrap, type FanslyAccount } from '../data'
import { useFanslyApi } from '../deps'
import { useProhibitedConfirm } from '../ui/useProhibited'
import { ChatHeader, type ChatActions, type ChatFlags } from './ChatHeader'
import { Composer } from './Composer'
import type { ChatMessage, ChatRow } from './model'
import { SideColumn } from './SideColumn'
import { Transcript, type Translation } from './Transcript'
import { useThread } from './useThread'

const SIDE_KEY = 'om.fsc.side'

function readSideOpen(): boolean {
  try {
    return localStorage.getItem(SIDE_KEY) !== '0'
  } catch {
    return true
  }
}

function saveSideOpen(open: boolean) {
  try {
    localStorage.setItem(SIDE_KEY, open ? '1' : '0')
  } catch {
    /* storage unavailable */
  }
}

/** Fansly's group ReportService location for a conversation (GroupService 4 × 1000 + 1). */
export const GROUP_REPORT_TYPE = 4001
/** Account ignore flags: 1 muted, 2 blocked. */
export const IGNORE_MUTE = 1
export const IGNORE_BLOCK = 2

export interface ChatPermissions {
  writeChats: boolean
  readLists: boolean
  writeLists: boolean
  readStats: boolean
}

interface Props {
  row: ChatRow
  /** The chat had unread messages when opened (they are acked once loaded). */
  unreadOnOpen: boolean
  meId: string
  can: ChatPermissions
  phone: boolean
  /** Phones: back to the list. */
  onBack?: () => void
  /** The chat was hidden: close it and drop it from the list. */
  onHidden: () => void
  /** Something changed in the chat (a send, new messages): the list refreshes. */
  onActivity: () => void
  /** Patch the chat's row in the list (read receipts, ignore flags). */
  onPatch: (patch: Partial<ChatRow>) => void
  /** Extra header buttons from the host (Workspace's pin, ADR 0045). */
  tools?: ReactNode
  /** Bumped by the host when a live event names this chat: the thread polls at once. */
  liveTick?: number
}

/** The open conversation: header, messages, composer and the fan column (a sheet on phones). */
export function ChatView({ row, unreadOnOpen, meId, can, phone, onBack, onHidden, onActivity, onPatch, tools, liveTick = 0 }: Props) {
  const { t, i18n } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const toast = useToast(3600)
  const prohibited = useProhibitedConfirm()
  const { groupId, fanId } = row
  const thread = useThread({ groupId, fanId, meId, canWrite: can.writeChats, unreadOnOpen, run: prohibited.run, notify: toast.say, onActivity })
  const { poll } = thread
  const seenTick = useRef(liveTick)
  useEffect(() => {
    if (liveTick === seenTick.current) return
    seenTick.current = liveTick
    poll()
  }, [liveTick, poll])

  // The fan's account (notes, ignore flags, last seen) — the list's copy until Fansly answers.
  const [fan, setFan] = useState<FanslyAccount | null>(row.account)
  useEffect(() => {
    if (!can.readLists) return
    let live = true
    api
      .get('/crm/accounts/by-ids', { ids: fanId })
      .then((p) => {
        const u = unwrap(p)
        const list = Array.isArray(u) ? asList(u) : asList((u as Record<string, unknown> | null)?.accounts)
        const found = list.map(accountOf).find((a) => a.id === fanId)
        if (live && found) setFan(found)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [api, fanId, can.readLists])

  const [ignoring, setIgnoring] = useState(row.ignoring)
  useEffect(() => {
    if (fan?.raw.ignoring != null) setIgnoring(num(fan.raw.ignoring))
  }, [fan])
  const [receipts, setReceipts] = useState(row.readReceipts !== 1)
  const flags: ChatFlags = { muted: (ignoring & IGNORE_MUTE) !== 0, blocked: (ignoring & IGNORE_BLOCK) !== 0, receipts }

  const setIgnore = useCallback(
    async (next: number) => {
      const before = ignoring
      setIgnoring(next)
      try {
        // Fansly's own Mute/Block in a conversation set the fan's ignore flags (account/ignore).
        await api.post('/crm/block', { ignoredId: fanId, ignoreFlags: next, confirm: true })
        onPatch({ ignoring: next })
      } catch (e) {
        setIgnoring(before)
        throw e
      }
    },
    [api, fanId, ignoring, onPatch],
  )

  const actions: ChatActions = {
    reload: thread.reload,
    toggleMute: () => setIgnore(ignoring ^ IGNORE_MUTE),
    toggleBlock: () => setIgnore(ignoring ^ IGNORE_BLOCK),
    toggleReceipts: async () => {
      const next = !receipts
      setReceipts(next)
      try {
        await api.post('/crm/chats/read-receipts', { groupId, sendReadReceipts: next })
        onPatch({ readReceipts: next ? 2 : 1 })
        toast.say(next ? t('header.receiptsOnDone') : t('header.receiptsOffDone'))
      } catch (e) {
        setReceipts(!next)
        throw e
      }
    },
    hide: async () => {
      await api.post('/crm/chats/hide', { groupId, confirm: true })
      onHidden()
    },
    report: async (reason, comment) => {
      await api.post('/crm/report', { contentId: groupId, contentType: GROUP_REPORT_TYPE, contentAccountId: fanId, reason, comment, confirm: true })
      toast.say(t('report.sent'))
    },
  }

  // Translations under messages, into the operator's language.
  const [translations, setTranslations] = useState<Map<string, Translation>>(new Map())
  const translate = useCallback(
    (m: ChatMessage) => {
      if (!m.content) return
      setTranslations((prev) => new Map(prev).set(m.id, { state: 'busy', text: '' }))
      api
        .translate(m.content, i18n.language.slice(0, 2) || 'en')
        .then((r) => setTranslations((prev) => new Map(prev).set(m.id, { state: 'done', text: r.same ? t('translate.same') : r.translated })))
        .catch((e: unknown) => setTranslations((prev) => new Map(prev).set(m.id, { state: 'error', text: (e as Error).message })))
    },
    [api, i18n.language, t],
  )

  const [insert, setInsert] = useState<{ text: string; n: number } | null>(null)
  const quote = useCallback((text: string) => setInsert((prev) => ({ text: `"${text.slice(0, 200)}"`, n: (prev?.n ?? 0) + 1 })), [])

  const [sideOpen, setSideOpen] = useState(() => (phone ? false : readSideOpen()))
  const toggleSide = () =>
    setSideOpen((v) => {
      if (!phone) saveSideOpen(!v)
      return !v
    })

  const side = useMemo(
    () => (
      <SideColumn
        row={row}
        fan={fan}
        meId={meId}
        can={{ writeChats: can.writeChats, readLists: can.readLists, writeLists: can.writeLists, readStats: can.readStats }}
        sheet={phone}
        onHide={toggleSide}
        notify={toast.say}
      />
    ),
    // toggleSide/toast.say are stable enough for the column's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row, fan, meId, can.writeChats, can.readLists, can.writeLists, can.readStats, phone],
  )

  return (
    <div className="rc-chat fsc-chat">
      <div className="rc-main">
        <ChatHeader row={{ ...row, name: fan?.name || row.name, avatar: fan?.avatar || row.avatar }} flags={flags} canWrite={can.writeChats} actions={actions} phone={phone} onBack={onBack} side={{ open: sideOpen, toggle: toggleSide }} refreshing={thread.loading} tools={tools} />
        <Transcript
          thread={thread}
          fanId={fanId}
          fanName={fan?.name || row.name || row.username}
          meId={meId}
          canWrite={can.writeChats}
          holdToAct={phone}
          translations={translations}
          onTranslate={translate}
          onQuote={can.writeChats ? quote : undefined}
        />
        {can.writeChats ? (
          <Composer groupId={groupId} fanId={fanId} fanName={fan?.name || row.name || row.username} thread={thread} phone={phone} insert={insert} />
        ) : (
          <div className="fsc-readonly" role="note">
            <FeatherIcon name="eye" size={14} />
            {t('composer.readOnly')}
          </div>
        )}
      </div>
      {!phone && sideOpen && side}
      {phone && (
        <PhoneSheet open={sideOpen} onClose={() => setSideOpen(false)} label={t('header.fanInfo')}>
          {sideOpen && <div className="ph-fan-sheet">{side}</div>}
        </PhoneSheet>
      )}
      {toast.node}
      {prohibited.node}
    </div>
  )
}
